// client/src/pages/MapPage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '../context/AuthContext';
import { useApi, api } from '../hooks/useApi';
import { useLocation } from 'react-router-dom';
import useMobile from '../hooks/useMobile';
import { useTheme } from '../context/ThemeContext';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

function calcAge(birthDate) {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (today.getMonth() < birth.getMonth() ||
      (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age--;
  return age;
}

function makeAvatarIcon(avatar, color, isSharing) {
  const html = `
    <div style="
      width:38px;height:38px;border-radius:50%;
      background:${color||'#7C6FF7'};
      border:3px solid ${isSharing ? '#34D399' : '#fff'};
      box-shadow:0 2px 8px rgba(0,0,0,.3);
      display:flex;align-items:center;justify-content:center;
      font-size:18px;line-height:1;
    ">${avatar||'?'}</div>`;
  return L.divIcon({ html, className:'', iconSize:[38,38], iconAnchor:[19,38], popupAnchor:[0,-40] });
}

function FlyTo({ center, flyKey }) {
  const map = useMap();
  useEffect(() => { if (center) map.flyTo(center, 14, { duration: 1.2 }); }, [flyKey, map]); // eslint-disable-line
  return null;
}

function parseTs(iso) {
  if (!iso) return null;
  // SQLite datetime('now') gir "YYYY-MM-DD HH:MM:SS" uten 'Z' — legg til Z for UTC-tolkning
  const s = iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z';
  return new Date(s);
}
function fmtTime(iso) {
  const d = parseTs(iso);
  if (!d || isNaN(d)) return '';
  const diff = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diff < 1)  return 'Nå';
  if (diff < 60) return `${diff} min siden`;
  if (diff < 1440) return `${Math.floor(diff/60)} t siden`;
  return d.toLocaleDateString('nb-NO', { day:'numeric', month:'short' });
}

// Lokasjoner eldre enn 2 timer regnes som foreldet
function isStale(updated_at) {
  const d = parseTs(updated_at);
  return !d || (Date.now() - d.getTime()) > 2 * 60 * 60 * 1000;
}

// Send JWT + coords til service worker så den kan POST i bakgrunnen
function syncToSW(token, coords) {
  if (!navigator.serviceWorker?.controller) return;
  if (token)  navigator.serviceWorker.controller.postMessage({ type: 'SET_TOKEN',  token });
  if (coords) navigator.serviceWorker.controller.postMessage({ type: 'SET_COORDS', coords });
}

// Registrer Periodic Background Sync (Chrome Android PWA)
async function registerPeriodicSync() {
  try {
    const reg = await navigator.serviceWorker.ready;
    if ('periodicSync' in reg) {
      const status = await navigator.permissions.query({ name: 'periodic-background-sync' });
      if (status.state === 'granted') {
        await reg.periodicSync.register('location-update', { minInterval: 5 * 60 * 1000 });
      }
    }
  } catch (_) {}
}

export default function MapPage() {
  const { user } = useAuth();
  const isMobile = useMobile();
  const { dark } = useTheme();
  const routerLocation = useLocation();
  const { data: allMembers, refresh } = useApi('/api/locations/all');
  const [sharing, setSharing]         = useState(false);
  const [gpsLoading, setGpsLoading]   = useState(false);
  const [flyTo, setFlyTo]             = useState(null);
  const [showMembers, setShowMembers] = useState(false);
  const [pingStatus, setPingStatus]   = useState({}); // userId → 'sending'|'sent'|'no_sub'
  const watchIdRef                    = useRef(null);
  const lastPostRef                   = useRef(0);

  const myAge       = calcAge(user?.birth_date);
  const isUnder16   = myAge !== null && myAge < 16;
  const isAdmin     = user?.role === 'admin';

  // Poll alle lokasjoner hvert 15. sekund
  useEffect(() => {
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [refresh]);

  // Sync sharing-state fra server; restart watchPosition automatisk hvis posisjonen er foreldet
  useEffect(() => {
    const mine = (allMembers || []).find(m => m.user_id === user?.id || m.id === user?.id);
    const serverSharing = !!mine?.sharing;
    setSharing(serverSharing);
    if (serverSharing && watchIdRef.current === null && navigator.geolocation && isStale(mine?.updated_at)) {
      startSharing();
    }
  }, [allMembers, user?.id]); // eslint-disable-line

  const postLocation = useCallback(async (pos) => {
    const now = Date.now();
    if (now - lastPostRef.current < 30000) return; // maks 1 gang per 30 sek
    lastPostRef.current = now;
    const { latitude: lat, longitude: lon, accuracy } = pos.coords;
    const place = await api.get(`/api/geocode/reverse?lat=${lat}&lon=${lon}`)
      .then(d => d.short_name).catch(() => null);
    await api.post('/api/locations', { lat, lon, accuracy, place_name: place, sharing: true });
    // Synce til SW for bakgrunns-oppdatering
    const token = localStorage.getItem('token');
    syncToSW(token, { lat, lon, accuracy, place_name: place });
    setSharing(true);
    setGpsLoading(false);
    refresh();
  }, [refresh]);

  const startSharing = useCallback(() => {
    if (!navigator.geolocation) return alert('GPS ikke støttet av nettleseren');
    setGpsLoading(true);
    // Første posisjon
    navigator.geolocation.getCurrentPosition(
      pos => { postLocation(pos); setFlyTo({ center: [pos.coords.latitude, pos.coords.longitude], key: Date.now() }); },
      err => { setGpsLoading(false); alert('Kunne ikke hente posisjon: ' + err.message); },
      { enableHighAccuracy: true, timeout: 12000 }
    );
    // Kontinuerlig overvåking
    if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
    watchIdRef.current = navigator.geolocation.watchPosition(
      postLocation,
      () => {},
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
  }, [postLocation]);

  const stopSharing = useCallback(async () => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    await api.delete('/api/locations');
    setSharing(false);
    refresh();
  }, [refresh]);

  // Under 16 eller ?autostart=1 (fra ping-notifikasjon): start automatisk
  useEffect(() => {
    const autostart = new URLSearchParams(routerLocation.search).get('autostart') === '1';
    if ((isUnder16 || autostart) && !sharing && !gpsLoading && navigator.geolocation) {
      startSharing();
    }
  }, [isUnder16]); // eslint-disable-line

  // Registrer periodic background sync og send token til SW
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) syncToSW(token, null);
    registerPeriodicSync();
  }, []); // eslint-disable-line

  // Auto-fokus: fly til egen posisjon når deling er aktiv, eller første delende medlem ved lasting
  useEffect(() => {
    if (!allMembers?.length) return;
    const own = allMembers.find(m => m.id === user?.id && m.sharing && m.lat != null);
    if (own) { setFlyTo({ center: [own.lat, own.lon], key: 0 }); return; }
    const first = allMembers.find(m => m.sharing && m.lat != null);
    if (first) setFlyTo({ center: [first.lat, first.lon], key: 0 });
  }, [allMembers]); // eslint-disable-line

  // Rydd opp watchPosition ved unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  // Keepalive: tving ny GPS-post hvert 5. minutt mens deling er aktiv
  useEffect(() => {
    if (!sharing) return;
    const t = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        postLocation,
        () => {},
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    }, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [sharing, postLocation]);

  // Mobil: tving ny posisjon når appen kommer tilbake i forgrunnen etter låst skjerm
  useEffect(() => {
    if (!sharing) return;
    const handleVisible = () => {
      if (document.visibilityState === 'visible') {
        navigator.geolocation.getCurrentPosition(
          postLocation,
          () => {},
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
        );
        refresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisible);
    return () => document.removeEventListener('visibilitychange', handleVisible);
  }, [sharing, postLocation, refresh]);

  // Stopp deling automatisk når fanen lukkes
  useEffect(() => {
    const handleUnload = () => { api.delete('/api/locations'); };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  // Admin: toggle deling for et familiemedlem
  const toggleMemberSharing = async (memberId, currentSharing) => {
    await api.put(`/api/locations/${memberId}/sharing`, { sharing: !currentSharing });
    refresh();
  };

  // Ping — be et familiemedlem om å dele posisjon
  const pingMember = async (memberId) => {
    setPingStatus(p => ({ ...p, [memberId]: 'sending' }));
    try {
      const res = await api.post(`/api/locations/ping/${memberId}`, {});
      setPingStatus(p => ({ ...p, [memberId]: res.ok ? 'sent' : 'no_sub' }));
    } catch {
      setPingStatus(p => ({ ...p, [memberId]: 'no_sub' }));
    }
    setTimeout(() => setPingStatus(p => { const n = {...p}; delete n[memberId]; return n; }), 4000);
  };

  const sharingMembers = (allMembers || []).filter(m => m.sharing && m.lat != null);
  const defaultCenter  = sharingMembers.length
    ? [sharingMembers[0].lat, sharingMembers[0].lon]
    : [59.9139, 10.7522];

  const pageBg = dark ? '#0A0818' : '#EDE8FF';
  const card   = dark ? 'rgba(255,255,255,.06)' : '#fff';
  const bdr    = dark ? 'rgba(255,255,255,.10)' : '#EAE8F8';
  const txt    = dark ? '#F0EEFF' : '#1A1A35';
  const sub    = dark ? '#A090C8' : '#A0A0B8';

  const memberList = (
    <div style={{ background: card, borderRadius: showMembers ? '0 0 12px 12px' : 0, border: `1px solid ${bdr}`, borderTop:'none', maxHeight:220, overflowY:'auto' }}>
      {(allMembers || []).map(m => {
        const age      = calcAge(m.birth_date);
        const under16  = age !== null && age < 16;
        const isMe     = m.id === user?.id;
        const canAdmin = isAdmin && under16 && !isMe;
        const stale    = isStale(m.updated_at);

        return (
          <div key={m.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 12px', borderBottom:`1px solid ${bdr}`, background: isMe ? (dark ? 'rgba(124,111,247,.12)' : '#F7F5FF') : 'transparent' }}>
            {/* Avatar */}
            <div style={{ position:'relative', flexShrink:0 }}>
              <div style={{ width:30, height:30, borderRadius:'50%', background:m.color||'#7C6FF7', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color:'#fff', overflow:'hidden' }}>
                {m.photo ? <img src={m.photo} alt="" style={{ width:'100%', height:'100%', borderRadius:'50%', objectFit:'cover' }}/> : (m.avatar||m.name?.[0]||'?')}
              </div>
              <div style={{ position:'absolute', bottom:0, right:0, width:8, height:8, borderRadius:'50%', border:'2px solid #fff', background: m.sharing ? (stale ? '#F59E0B' : '#34D399') : '#E0E0E0' }}/>
            </div>

            {/* Info */}
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:12, fontWeight:700, color:txt, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {m.name}{isMe ? ' (meg)' : ''}
              </div>
              <div style={{ fontSize:10, color: stale && m.sharing ? '#F59E0B' : sub, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {m.sharing && m.lat
                  ? (stale ? `⚠ ${fmtTime(m.updated_at)}` : (m.place_name || fmtTime(m.updated_at)))
                  : under16 ? '⛔ Deling av' : 'Deler ikke'}
              </div>
            </div>

            {/* Handlinger */}
            <div style={{ display:'flex', gap:4, alignItems:'center', flexShrink:0 }}>
              {m.sharing && m.lat && (
                <button style={{ background:'none', border:`1px solid ${bdr}`, borderRadius:7, padding:'2px 6px', cursor:'pointer', fontSize:12, fontFamily:'inherit', color:txt }} onClick={() => setFlyTo({ center:[m.lat,m.lon], key:Date.now() })}>📍</button>
              )}
              {!isMe && (
                <button
                  style={{ padding:'2px 8px', borderRadius:7, border:'none', cursor:'pointer', fontSize:10, fontWeight:700, fontFamily:'inherit',
                    background: pingStatus[m.id]==='sent' ? '#D1FAE5' : pingStatus[m.id]==='no_sub' ? '#FEE2E2' : pingStatus[m.id]==='sending' ? '#F5F5F8' : '#EDE8FF',
                    color:      pingStatus[m.id]==='sent' ? '#065F46' : pingStatus[m.id]==='no_sub' ? '#C0312D' : '#7C6FF7' }}
                  disabled={!!pingStatus[m.id]}
                  onClick={() => pingMember(m.id)}>
                  {pingStatus[m.id]==='sending' ? '⏳' : pingStatus[m.id]==='sent' ? '✓' : pingStatus[m.id]==='no_sub' ? '✗' : '📣'}
                </button>
              )}
              {canAdmin && (
                <button style={{ padding:'2px 8px', borderRadius:7, border:'none', cursor:'pointer', fontSize:10, fontWeight:700, fontFamily:'inherit', background: m.sharing ? '#FEE2E2' : '#D1FAE5', color: m.sharing ? '#C0312D' : '#065F46' }}
                  onClick={() => toggleMemberSharing(m.id, m.sharing)}>
                  {m.sharing ? 'Av' : 'På'}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div style={{ height:'100%', background: pageBg, display:'flex', flexDirection:'column' }}>

      {/* Gradient hero-header */}
      <div style={{ background:'linear-gradient(135deg,#7C6FF7 0%,#5B4FCF 100%)', padding: isMobile ? '48px 16px 12px' : '20px 20px 12px', flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize: isMobile ? 18 : 20, fontWeight:800, color:'#fff' }}>🗺 Familiekart</div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,.75)', marginTop:1 }}>
              {sharingMembers.length} av {(allMembers||[]).length} deler posisjon
            </div>
          </div>
          <div>
            {!isUnder16 && (
              sharing
                ? <button style={{ padding:'7px 14px', borderRadius:10, border:'1px solid rgba(255,255,255,.3)', background:'rgba(255,255,255,.15)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:12, fontFamily:'inherit', backdropFilter:'blur(8px)' }} onClick={stopSharing}>🔴 Slutt</button>
                : <button style={{ padding:'7px 14px', borderRadius:10, border:'none', background:'#34D399', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:12, fontFamily:'inherit' }} onClick={startSharing} disabled={gpsLoading}>
                    {gpsLoading ? '📍 Henter…' : '📍 Del posisjon'}
                  </button>
            )}
            {isUnder16 && (
              <div style={{ fontSize:11, fontWeight:700, color:'#fff', background:'rgba(255,255,255,.2)', padding:'5px 10px', borderRadius:8 }}>
                {sharing ? '✅ Deler' : gpsLoading ? '📍 Starter…' : '📍 Auto'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Innhold */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', gap:8, padding: isMobile ? '8px 14px 14px' : '12px 14px 14px', minHeight:0 }}>

        {/* Familiemedlemmer — dropdown */}
        <div style={{ flexShrink:0 }}>
          <button
            onClick={() => setShowMembers(v => !v)}
            style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px', background: card, border:`1px solid ${bdr}`, borderRadius: showMembers ? '10px 10px 0 0' : 10, cursor:'pointer', fontFamily:'inherit' }}>
            <span style={{ fontSize:12, fontWeight:700, color:txt }}>
              👨‍👩‍👧 Familiemedlemmer
              <span style={{ marginLeft:6, fontSize:10, fontWeight:600, color:sub }}>
                {sharingMembers.length}/{(allMembers||[]).length} deler
              </span>
            </span>
            <span style={{ fontSize:11, color:sub, transform: showMembers ? 'rotate(180deg)' : 'none', transition:'transform .2s' }}>▼</span>
          </button>
          {showMembers && memberList}
        </div>

        {/* Kart */}
        <div style={{ flex:1, borderRadius:12, overflow:'hidden', border:`1px solid ${bdr}`, height: isMobile ? 'calc(100vh - 220px)' : 'calc(100vh - 200px)' }}>
          <MapContainer
            center={defaultCenter}
            zoom={sharingMembers.length ? 11 : 6}
            style={{ width:'100%', height:'100%' }}
            zoomControl={true}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
            {flyTo && <FlyTo center={flyTo.center || flyTo} flyKey={flyTo.key || 0}/>}
            {sharingMembers.map(loc => (
              <Marker key={loc.id || loc.user_id}
                position={[loc.lat, loc.lon]}
                icon={makeAvatarIcon(loc.avatar, loc.color, !isStale(loc.updated_at))}>
                <Popup>
                  <div style={s.popup}>
                    <div style={s.popupName}>{loc.name}</div>
                    {loc.place_name && <div style={s.popupPlace}>📍 {loc.place_name}</div>}
                    <div style={s.popupTime}>🕐 {fmtTime(loc.updated_at)}</div>
                    {loc.accuracy && <div style={s.popupAcc}>Nøyaktighet: ±{Math.round(loc.accuracy)}m</div>}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

      </div>
    </div>
  );
}

const s = {
  popup:     { minWidth:130 },
  popupName: { fontWeight:700, fontSize:13, marginBottom:3 },
  popupPlace:{ fontSize:11, color:'#6B6B8A', marginBottom:2 },
  popupTime: { fontSize:11, color:'#A0A0B8' },
  popupAcc:  { fontSize:10, color:'#C0C0D0', marginTop:2 },
};
